import { motion } from "framer-motion";
import { Link2, ArrowLeft, Check, Smartphone, User, PawPrint, Store } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";

const paths = [
  { icon: User, path: "/u/:id", label: "Profili utente" },
  { icon: PawPrint, path: "/cane/:id", label: "Schede cane" },
  { icon: Store, path: "/partner/:id", label: "Negozi e strutture partner" },
];

const files = [
  { name: "apple-app-site-association", href: "/.well-known/apple-app-site-association", note: "iOS Universal Links" },
  { name: "assetlinks.json", href: "/.well-known/assetlinks.json", note: "Android App Links" },
];

export default function UniversalLinks() {
  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#1A1A1A]" data-testid="universal-links-page">
      <nav className="flex items-center justify-between px-6 sm:px-10 lg:px-16 py-6 border-b border-[#1A1A1A]/[0.06]">
        <Logo testId="universal-links-logo" />
        <Link
          to="/"
          data-testid="universal-links-back-link"
          className="flex items-center gap-2 text-sm font-semibold text-[#5C5C5C] hover:text-[#FF6B35] transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna alla home
        </Link>
      </nav>

      <main className="px-6 sm:px-10 lg:px-16 py-16 lg:py-24 max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-[#FF6B35] mb-6">
            <Link2 className="w-4 h-4" />
            Configurazione tecnica
          </p>
          <h1
            data-testid="universal-links-title"
            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
          >
            Universal Links
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#5C5C5C] leading-relaxed">
            fuffyfam.com è configurato come dominio di universal link per l'app FuffyFam:
            i link condivisi aprono direttamente l'app se installata, altrimenti questo sito.
          </p>
        </motion.div>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14"
          data-testid="universal-links-paths"
        >
          <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">Percorsi gestiti</h2>
          <ul className="mt-6 space-y-4">
            {paths.map(({ icon: Icon, path, label }) => (
              <li
                key={path}
                className="flex items-center gap-4 bg-white border border-[#1A1A1A]/10 rounded-2xl px-5 py-4"
              >
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#FF6B35]/10 shrink-0">
                  <Icon className="w-5 h-5 text-[#FF6B35]" />
                </span>
                <div>
                  <p className="font-mono text-sm font-semibold text-[#1A1A1A]">fuffyfam.com{path}</p>
                  <p className="text-sm text-[#5C5C5C]">{label}</p>
                </div>
              </li>
            ))}
          </ul>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12"
          data-testid="universal-links-files"
        >
          <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">File di verifica</h2>
          <ul className="mt-6 space-y-4">
            {files.map((f) => (
              <li
                key={f.name}
                className="flex items-center gap-4 bg-white border border-[#1A1A1A]/10 rounded-2xl px-5 py-4"
              >
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#1A1A1A] shrink-0">
                  <Check className="w-5 h-5 text-[#FF6B35]" />
                </span>
                <div className="min-w-0">
                  <a
                    href={f.href}
                    data-testid={`verification-file-${f.name}`}
                    className="font-mono text-sm font-semibold text-[#FF6B35] hover:text-[#E55A2B] transition-colors duration-300 break-all"
                  >
                    /.well-known/{f.name}
                  </a>
                  <p className="text-sm text-[#5C5C5C]">{f.note}</p>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-6 flex items-start gap-3 text-sm leading-relaxed text-[#5C5C5C]">
            <Smartphone className="w-4 h-4 mt-0.5 shrink-0 text-[#FF6B35]" />
            I file sono pubblici e serviti via HTTPS, come richiesto da Apple e Google per la
            verifica del dominio. L'associazione con l'app si attiva al momento della pubblicazione
            su App Store e Google Play.
          </p>
        </motion.section>
      </main>

      <footer className="border-t border-[#1A1A1A]/[0.08] px-6 sm:px-10 lg:px-16 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 max-w-3xl mx-auto">
        <p className="text-sm text-[#5C5C5C]">© 2026 FuffyFam · fuffyfam.com</p>
        <a
          href="mailto:supporto.fuffyfam@gmail.com"
          data-testid="universal-links-contact-link"
          className="text-sm font-semibold text-[#FF6B35] hover:text-[#E55A2B] transition-colors duration-300"
        >
          supporto.fuffyfam@gmail.com
        </a>
      </footer>
    </div>
  );
}
